import React from 'react';
import { motion } from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import { Search, Shield, Handshake, Target, FileText } from 'lucide-react';

const Features = () => {
  const navigate = useNavigate();
  const features = [
    { 
      title: "Approche basée sur des attaques réelles", 
      desc: "Nous simulons les méthodes utilisées par de vrais attaquants pour identifier les vulnérabilités exploitables avant qu'elles ne soient utilisées contre vous.", 
      icon: Target 
    },
    { 
      title: "Identification proactive des vulnérabilités", 
      desc: "Nous détectons les expositions et failles critiques avant qu'elles ne deviennent des points d'entrée pour une cyberattaque.", 
      icon: Search 
    },
    { 
      title: "Rapports clairs et exploitables", 
      desc: "Chaque mission donne lieu à un rapport détaillé, compréhensible et directement utilisable par vos équipes techniques et décisionnelles.", 
      icon: FileText 
    },
    { 
      title: "Confidentialité et sécurité des données", 
      desc: "Toutes les informations collectées sont traitées de manière strictement confidentielle avec des protocoles de sécurité rigoureux.", 
      icon: Shield 
    },
    { 
      title: "Recommandations et accompagnement", 
      desc: "Nous fournissons des actions claires, priorisées et directement applicables pour réduire vos risques cyber rapidement.", 
      icon: Handshake 
    }
  ];

  return (
    <section className="relative w-full bg-[#030712] py-16 px-6 md:py-24 overflow-hidden">
      
      {/* --- EFFETS DE LUMIÈRE RÉDUITS --- */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[300px] h-[300px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[250px] h-[250px] bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto">
        
        {/* Header Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#3b82f6] text-[11px] md:text-[12px] font-black uppercase tracking-[0.25em] mb-3 block">
            Une approche orientée résultats et sécurité réelle
          </span>
          <h2 className="text-white text-[28px] md:text-[34px] font-black tracking-tight mb-3">
            Pourquoi Nodixs ?
          </h2>
          <p className="text-slate-400 text-[14px] md:text-[15px] max-w-xl mx-auto mb-6 font-medium italic opacity-80">
            "Une mauvaise visibilité sur votre exposition peut suffire à compromettre votre sécurité."
          </p>
          <div className="w-10 h-[2px] bg-[#3b82f6] mx-auto rounded-full shadow-[0_0_10px_#3b82f6]" />
        </motion.div>

        {/* Grille Flexbox pour un contrôle total du centrage sur la 2ème ligne */}
        <div className="flex flex-wrap justify-center gap-5">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                // w-full sur mobile, 31% pour en mettre 3 par ligne sur desktop
                className="relative group w-full md:w-[calc(33.33%-1.25rem)] lg:max-w-[340px]"
              >
                <div className="absolute -inset-[1px] bg-gradient-to-b from-blue-500/15 to-transparent rounded-[20px] opacity-40 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative h-full bg-[#0a0f1d]/50 backdrop-blur-sm border border-white/5 p-6 rounded-[20px] overflow-hidden flex flex-col">
                  
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(59,130,246,0.08),transparent_40%)]" />

                  <div className="relative z-10 bg-blue-500/10 p-2.5 rounded-lg w-fit mb-5 text-[#3b82f6]">
                    <Icon size={18} strokeWidth={2.5} />
                  </div>
                  
                  <h3 className="relative z-10 text-white text-[17px] md:text-[18px] font-bold mb-3 font-inter leading-snug">
                    {feature.title}
                  </h3>
                  
                  <p className="relative z-10 text-slate-400 text-[13px] md:text-[14px] leading-relaxed font-medium font-inter mt-auto opacity-90">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bouton CTA Final Plus Petit */}
        <div className="mt-16 flex justify-center">
          <motion.button 
            onClick={() => navigate('/rdv')}
            whileHover={{ scale: 1.03, shadow: "0 0 20px rgba(59,130,246,0.4)" }}
            whileTap={{ scale: 0.97 }}
            className="bg-[#3b82f6] text-white px-8 py-3.5 rounded-lg font-bold text-[13px] uppercase tracking-wider shadow-lg transition-all"
          >
            Évaluer mon exposition
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default Features;